/**
 * HIST-EQUIP-01 (Seth 2026-09-01: "history should show which equipment was
 * worked on when expanded for details").
 *
 * What the data can HONESTLY say, per visit:
 *
 *   installed — units this visit's invoice CREATED. ESC links an invoice line
 *               to the Equip row it spawned (`mirror_sales_line.eq_counter`) —
 *               97% of install invoices carry it, ~2% of service invoices (a
 *               thermostat sold on a call). Pegasus-native: `pegasus_equipment`
 *               rows born on the visit (`origin_dispatch`).
 *   worked    — units the tech tapped in the field app's "What are you working
 *               on?" step (`pegasus_field_status.worked_equipment`). ESC never
 *               recorded this for service calls, so ESC-era visits have none —
 *               and we say so rather than guess from the notes (the
 *               humidifier-pad rule: notes-guessing tells the tech what the
 *               customer HAS, not what we TOUCHED).
 *
 * One query per customer; both the office 360 (History rows + DispatchPeek)
 * and the phone's History tab read the same map, keyed by every id a visit
 * goes by (ESC dispatch #, Pegasus dispatch #, Pegasus dispatch uuid, and the
 * invoice numbers).
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface VisitUnit {
  /** Stable key — `esc-<counter>` or `peg-<uuid>`. */
  key: string;
  /** "LENNOX EL16KC1-042-230A04" — brand + model, or the category. */
  label: string;
  mfg: string | null;
  model: string | null;
  serial: string | null;
  category: string | null;
  source: "esc" | "peg";
}

export interface VisitEquipment {
  installed: VisitUnit[];
  worked: VisitUnit[];
}

export interface VisitEquipmentIndex {
  /** Keyed by ESC dispatch #, Pegasus dispatch #, AND Pegasus dispatch uuid. */
  byDispatch: Map<string, VisitEquipment>;
  /** Keyed by ESC invoice number (zero-padded, as mirrored). */
  byInvoice: Map<string, VisitEquipment>;
}

const EMPTY: VisitEquipmentIndex = { byDispatch: new Map(), byInvoice: new Map() };

const clean = (s: string | null | undefined) => (s ?? "").toString().trim();
const unitLabel = (mfg: string | null, model: string | null, category: string | null) =>
  [clean(mfg), clean(model)].filter(Boolean).join(" ") || clean(category) || "Unit";

function upsert(map: Map<string, VisitEquipment>, key: string): VisitEquipment {
  const k = clean(key);
  let v = map.get(k);
  if (!v) {
    v = { installed: [], worked: [] };
    map.set(k, v);
  }
  return v;
}
function pushUnique(list: VisitUnit[], u: VisitUnit) {
  if (!list.some((x) => x.key === u.key)) list.push(u);
}

export function useVisitEquipment(custno: string | null | undefined) {
  return useQuery({
    queryKey: ["visit_equipment", custno],
    enabled: !!custno,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<VisitEquipmentIndex> => {
      if (!custno) return EMPTY;

      const [salesRes, escEquipRes, pegEquipRes, fieldRes, pegDispRes] = await Promise.all([
        supabase
          .from("mirror_sales")
          .select("invoice, dispatch")
          .eq("custno", custno)
          .order("inv_date", { ascending: false })
          .limit(400),
        supabase
          .from("mirror_equipment")
          .select("counter, mfg, model, serial, eq_type, invoice")
          .eq("custno", custno)
          .limit(800),
        supabase
          .from("pegasus_equipment")
          .select("id, brand, model, serial, category, origin_dispatch, status")
          .eq("custno", custno)
          .limit(200),
        supabase
          .from("pegasus_field_status")
          .select("dispatch_ref, source, worked_equipment")
          .eq("custno", custno)
          .not("worked_equipment", "is", null)
          .limit(300),
        supabase
          .from("pegasus_dispatch")
          .select("id, dispatch_no")
          .eq("custno", custno)
          .is("deleted_at", null)
          .limit(300),
      ]);

      const sales = (salesRes.data ?? []) as { invoice: string; dispatch: string | null }[];
      const inv2disp = new Map<string, string>();
      for (const s of sales) if (clean(s.dispatch)) inv2disp.set(clean(s.invoice), clean(s.dispatch));

      // ESC units by counter (trimmed — ESC pads to 8, lines sometimes don't).
      const escUnits = new Map<string, VisitUnit>();
      const escByInvoice = new Map<string, VisitUnit[]>();
      for (const r of (escEquipRes.data ?? []) as {
        counter: string; mfg: string | null; model: string | null; serial: string | null; eq_type: string | null; invoice: string | null;
      }[]) {
        const counter = clean(r.counter);
        if (!counter) continue;
        const u: VisitUnit = {
          key: `esc-${counter}`,
          label: unitLabel(r.mfg, r.model, r.eq_type),
          mfg: clean(r.mfg) || null,
          model: clean(r.model) || null,
          serial: clean(r.serial) || null,
          category: clean(r.eq_type) || null,
          source: "esc",
        };
        escUnits.set(counter, u);
        escUnits.set(counter.replace(/^0+/, "") || counter, u);
        // The install-invoice link on the Equip row itself.
        const inv = clean(r.invoice);
        if (inv) {
          const list = escByInvoice.get(inv) ?? [];
          list.push(u);
          escByInvoice.set(inv, list);
        }
      }

      // Pegasus-native units.
      const pegUnits = new Map<string, VisitUnit>();
      const pegByOrigin = new Map<string, VisitUnit[]>();
      for (const r of (pegEquipRes.data ?? []) as {
        id: string; brand: string | null; model: string | null; serial: string | null; category: string | null; origin_dispatch: string | null; status: string | null;
      }[]) {
        const u: VisitUnit = {
          key: `peg-${r.id}`,
          label: unitLabel(r.brand, r.model, r.category),
          mfg: clean(r.brand) || null,
          model: clean(r.model) || null,
          serial: clean(r.serial) || null,
          category: clean(r.category) || null,
          source: "peg",
        };
        pegUnits.set(r.id, u);
        const origin = clean(r.origin_dispatch);
        if (origin) {
          const list = pegByOrigin.get(origin) ?? [];
          list.push(u);
          pegByOrigin.set(origin, list);
        }
      }

      // Pegasus dispatch ids ↔ spoken numbers, so every alias lands on one entry.
      const uuid2no = new Map<string, string>();
      for (const d of (pegDispRes.data ?? []) as { id: string; dispatch_no: number | null }[]) {
        if (d.dispatch_no != null) uuid2no.set(d.id, String(d.dispatch_no));
      }
      const aliases = (ref: string): string[] => {
        const r = clean(ref);
        if (!r) return [];
        const no = uuid2no.get(r);
        return no ? [r, no] : [r];
      };

      const byDispatch = new Map<string, VisitEquipment>();
      const byInvoice = new Map<string, VisitEquipment>();

      // ESC: invoice lines that created equipment → installed on that visit.
      const invoices = sales.map((s) => clean(s.invoice)).filter(Boolean);
      if (invoices.length > 0) {
        const { data: lines } = await supabase
          .from("mirror_sales_line")
          .select("invoice, eq_counter")
          .in("invoice", invoices)
          .not("eq_counter", "is", null)
          .limit(2000);
        for (const l of (lines ?? []) as { invoice: string; eq_counter: string | null }[]) {
          const counter = clean(l.eq_counter);
          if (!counter || counter === "0") continue;
          const u = escUnits.get(counter) ?? escUnits.get(counter.replace(/^0+/, ""));
          if (!u) continue;
          const inv = clean(l.invoice);
          pushUnique(upsert(byInvoice, inv).installed, u);
          const disp = inv2disp.get(inv);
          if (disp) pushUnique(upsert(byDispatch, disp).installed, u);
        }
      }
      // ESC: the Equip row's own install-invoice pointer (same story, other direction).
      for (const [inv, units] of escByInvoice) {
        const entry = upsert(byInvoice, inv);
        for (const u of units) pushUnique(entry.installed, u);
        const disp = inv2disp.get(inv);
        if (disp) for (const u of units) pushUnique(upsert(byDispatch, disp).installed, u);
      }
      // Pegasus: units added in the field on a visit.
      for (const [origin, units] of pegByOrigin) {
        for (const alias of aliases(origin)) {
          const entry = upsert(byDispatch, alias);
          for (const u of units) pushUnique(entry.installed, u);
        }
      }
      // Pegasus: what the tech said they worked on.
      for (const f of (fieldRes.data ?? []) as {
        dispatch_ref: string; source: string; worked_equipment: Array<{ k?: string; label?: string }> | null;
      }[]) {
        const units = Array.isArray(f.worked_equipment) ? f.worked_equipment : [];
        if (units.length === 0) continue;
        const resolved: VisitUnit[] = units.map((w) => {
          const k = clean(w.k);
          const esc = k.startsWith("esc-") ? escUnits.get(k.slice(4)) : undefined;
          const peg = k.startsWith("peg-") ? pegUnits.get(k.replace(/^peg-(peg:)?/, "")) : undefined;
          const hit = esc ?? peg;
          return hit ?? {
            key: k || `label-${clean(w.label)}`,
            label: clean(w.label) || "Unit",
            mfg: null, model: null, serial: null, category: null,
            source: k.startsWith("peg-") ? "peg" : "esc",
          };
        });
        for (const alias of aliases(f.dispatch_ref)) {
          const entry = upsert(byDispatch, alias);
          for (const u of resolved) pushUnique(entry.worked, u);
        }
      }

      return { byDispatch, byInvoice };
    },
  });
}

/** Look a visit up by any of its ids (dispatch #, uuid) or its invoice #. */
export function visitEquipmentFor(
  idx: VisitEquipmentIndex | undefined,
  ids: Array<string | null | undefined>,
): VisitEquipment | null {
  if (!idx) return null;
  for (const id of ids) {
    const k = clean(id);
    if (!k) continue;
    const hit = idx.byDispatch.get(k) ?? idx.byInvoice.get(k) ?? idx.byDispatch.get(k.replace(/^0+/, "")) ?? idx.byInvoice.get(k.padStart(10, "0"));
    if (hit && (hit.installed.length || hit.worked.length)) return hit;
  }
  return null;
}
